import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
    fetchDistributorRetailerStats,
    fetchDistributorRetailers,
} from '../../../../services/Http/DistributorApi';
import StatCard from '../../../../shared/layout/cards/StatCard';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import { MdStorefront, MdTrendingUp, MdInventory } from 'react-icons/md';
import { useCurrency } from '../../../../context/CurrencyProvider';

const statsConfig = [
    { key: 'total_retailers', label: 'DistributorRetailers.stats.totalRetailers', icon: MdStorefront, format: 'number', color: 'retailers' },
    { key: 'outbound_value', label: 'DistributorRetailers.stats.outboundValue', icon: MdTrendingUp, format: 'currency', color: 'totalVolume' },
    { key: 'outbound_quantity', label: 'DistributorRetailers.stats.outboundQuantity', icon: MdInventory, format: 'number', color: 'transactions' },
];

const PER_PAGE = 10;

const DistributorRetailersList = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [stats, setStats] = useState(null);
    const [list, setList] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, current_page: 1, last_page: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const formatAmount = useCallback((value, localAmount = null, localCurrencyId = null, localCurrencyCode = null) => {
        return formatResolvedAmount({
            eurAmount: value,
            localAmount,
            localCurrencyId,
            localCurrencyCode,
        });
    }, [formatResolvedAmount]);

    const loadData = async (page = 1, searchTerm = '') => {
        setLoading(true);
        try {
            const [statsResponse, listResponse] = await Promise.all([
                fetchDistributorRetailerStats(),
                fetchDistributorRetailers({ page, perPage: PER_PAGE, search: searchTerm }),
            ]);
            setStats(statsResponse);
            setList(listResponse.data || []);
            setPagination(listResponse.pagination || { total: 0, current_page: 1, last_page: 1 });
            setError(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleSearch = (event) => {
        const value = event.target.value;
        setSearch(value);
        loadData(1, value);
    };

    const handlePageChange = (direction) => {
        const nextPage = pagination.current_page + direction;
        if (nextPage < 1 || nextPage > pagination.last_page) {
            return;
        }
        loadData(nextPage, search);
    };

    const formattedStats = useMemo(() => stats || {}, [stats]);
    const formatValue = (value, formatType = 'number') => {
        if (value === null || value === undefined) {
            return '0';
        }
        const numeric = Number(value);
        if (Number.isNaN(numeric)) {
            return value;
        }
        if (formatType === 'currency') {
            return formatAmount(
                numeric,
                formattedStats.outbound_value_local,
                formattedStats.outbound_currency_id
            );
        }
        return numeric.toLocaleString();
    };

    if (loading) {
        return <LoadingMessage>{t('DistributorRetailers.loading')}</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>{t('DistributorRetailers.error')}</ErrorMessage>;
    }

    return (
        <div>
            <HeaderRow>
                <div>
                    <SectionTitle>{t('DistributorRetailers.list.title')}</SectionTitle>
                    <Subtitle>{t('DistributorRetailers.subtitle')}</Subtitle>
                </div>
                <SearchInput
                    type="search"
                    value={search}
                    onChange={handleSearch}
                    placeholder={t('DistributorRetailers.searchPlaceholder')}
                />
            </HeaderRow>
            <StatsRow>
                {statsConfig.map(({ key, label, icon: Icon, format, color }) => (
                    <StatCard
                        key={key}
                        icon={<Icon className="icon" />}
                        title={t(label)}
                        value={formatValue(formattedStats[key], format)}
                        color={color}
                    />
                ))}
            </StatsRow>

            <TableCard>
                <TableHeader>
                    <h4>{t('DistributorRetailers.table.title')}</h4>
                    <span>{t('DistributorRetailers.table.count', { count: pagination.total })}</span>
                </TableHeader>
                <Table>
                    <thead>
                        <tr>
                            <th>{t('DistributorRetailers.table.retailer')}</th>
                            <th>{t('DistributorRetailers.table.manufacturers')}</th>
                            <th>{t('DistributorRetailers.table.outbound')}</th>
                            <th>{t('DistributorRetailers.table.categories')}</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((retailer) => (
                            <tr key={retailer.id}>
                                <td>{retailer.name}</td>
                                <td>{retailer.manufacturers_count}</td>
                                <td>
                                    <ValueStack>
                                        <span>{formatAmount(
                                            retailer.outbound_value,
                                            retailer.outbound_value_local,
                                            retailer.seller_currency_id
                                        )}</span>
                                        <small>{t('DistributorRetailers.table.units', { count: retailer.outbound_quantity })}</small>
                                    </ValueStack>
                                </td>
                                <td>
                                    {formatCategories(retailer.categories, t)}
                                </td>
                                <td>
                                    <DetailLink to={`/retailers/${retailer.id}`}>
                                        {t('DistributorRetailers.table.view')}
                                    </DetailLink>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                <Pagination>
                    <button onClick={() => handlePageChange(-1)} disabled={pagination.current_page <= 1}>
                        {t('DistributorRetailers.pagination.prev')}
                    </button>
                    <span>
                        {pagination.current_page} / {pagination.last_page}
                    </span>
                    <button onClick={() => handlePageChange(1)} disabled={pagination.current_page >= pagination.last_page}>
                        {t('DistributorRetailers.pagination.next')}
                    </button>
                </Pagination>
            </TableCard>
        </div>
    );
};

const formatCategories = (categories, t) => {
    const clean = (categories || '').trim();
    if (!clean) {
        return t('DistributorRetailers.table.allCategories');
    }
    return clean.split(',').slice(0, 3).join(', ');
};

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1rem;
    margin-bottom: 1rem;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors.brand || '#821692'};
`;

const Subtitle = styled.p`
    margin: 0.25rem 0 0;
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

const SearchInput = styled.input`
    border: 1px solid rgba(130, 22, 146, 0.2);
    padding: 0.625rem 0.875rem;
    border-radius: 8px;
    min-width: 240px;
`;

const StatsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const TableCard = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    padding: 1.25rem;
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        text-align: left;
        padding: 0.75rem 0;
    }

    tbody tr + tr {
        border-top: 1px solid rgba(0, 0, 0, 0.05);
    }
`;

const ValueStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;

    small {
        color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
    }
`;

const DetailLink = styled(Link)`
    color: #821692;
    font-weight: 600;
`;

const Pagination = styled.div`
    margin-top: 1rem;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;

    button {
        border: none;
        background: rgba(130, 22, 146, 0.1);
        color: #821692;
        padding: 0.5rem 0.75rem;
        border-radius: 6px;
        cursor: pointer;
    }

    button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

export default DistributorRetailersList;
